<template>
  <div
    :class="[
      'data-property',
      directionClass,
      sizeClass,
      { 'data-property-bold': isBold },
      { 'data-property-border': hasBorder },
      { 'data-property-invalid': isInvalid },
      $attrs.class,
    ]"
    data-cy="data-property"
  >
    <p
      class="data-property__label"
      data-cy="data-property-label"
    >
      <app-icon
        v-if="icon"
        :name="icon"
        size="sm"
        :class="isInvalid ? 'text-error-600' : 'text-gray-500'"
      />
      <slot name="label">
        {{ label }}
      </slot>
    </p>
    <span
      v-if="hasLeader && !isLoading && direction === 'horizontal'"
      class="app-leader"
    />
    <div
      v-if="isLoading"
      class="data-property__skeleton skeleton"
      :class="loadingSkeletonClass"
      data-cy="data-property-skeleton"
    />
    <p
      v-else
      :class="['data-property__value', { 'is-truncated': isTruncated }, { 'justify-between': spaceBetween }]"
      data-cy="data-property-value"
    >
      <slot
        name="value"
        :is-copied="isCopied"
      >
        <app-icon
          v-if="isInvalid"
          size="sm"
          name="alert-circle"
          class="text-error-600"
        />
        <span>{{ displayValue }}</span>
      </slot>
      <button
        v-if="canCopy"
        class="btn btn-link"
        @click="handleCopy(value)"
        data-cy="data-property-copy-button"
      >
        <app-icon
          name="copy"
          size="sm"
        />
      </button>
    </p>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue';

const props = defineProps({
  icon: {
    type: String,
    default: '',
  },
  label: {
    type: String,
    default: '',
  },
  value: {
    type: [String, Number],
    default: '',
  },
  direction: {
    type: String,
    default: 'vertical',
    validator: (value) => ['vertical', 'horizontal'].includes(value),
  },
  size: {
    type: String,
    default: 'md',
    validator: (value) => ['sm', 'md', 'lg'].includes(value),
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  isBold: {
    type: Boolean,
    default: false,
  },
  hasBorder: {
    type: Boolean,
    default: false,
  },
  spaceBetween: {
    type: Boolean,
    default: false,
  },
  canCopy: {
    type: Boolean,
    default: false,
  },
  hasLeader: {
    type: Boolean,
    default: false,
  },
  isTruncated: {
    type: Boolean,
    default: false,
  },
  isInvalid: {
    type: Boolean,
    default: false,
  },
  loadingSkeletonClass: {
    type: String,
    default: '',
  },
});

const isCopied = ref(false);
let copyTimeout = null;

const directionClass = computed(() => {
  const classes = {
    vertical: 'data-property-col',
    horizontal: 'data-property-horizontal',
  };
  return classes[props.direction];
});

const sizeClass = computed(() => {
  const classes = {
    sm: 'data-property-sm',
    md: 'data-property-md',
    lg: 'data-property-lg',
  };
  return classes[props.size];
});

const displayValue = computed(() => {
  return isCopied.value ? 'Copied!' : props.value;
});

const handleCopy = async (val) => {
  await navigator.clipboard.writeText(val);
  isCopied.value = true;

  if (copyTimeout) {
    clearTimeout(copyTimeout);
  }

  copyTimeout = setTimeout(() => {
    isCopied.value = false;
  }, 1500);
};
</script>

<style>
@reference '@/assets/main.css';

.data-property {
  @apply flex flex-col gap-0.5;

  &.data-property-sm {
    @apply text-xs;
  }

  &.data-property-md {
    @apply text-sm;
  }

  &.data-property-lg {
    @apply text-base;
  }

  &.data-property-horizontal {
    @apply flex-row justify-between gap-2;

    & .data-property__skeleton {
      @apply flex-1 max-w-[200px];
    }
  }

  &.data-property-bold {
    & .data-property__label {
      @apply font-medium text-base-content;
    }
  }

  &.data-property-invalid {
    & .data-property__label {
      @apply text-error-700;
    }

    & .data-property__value {
      @apply text-error-700;
    }
  }

  /*
    Padding is applied where component is declared, which is weird, but flexible,
    since the gap is defined on the parent wrapper
  */
  &.data-property-border:not(:last-of-type) {
    @apply border-b border-gray-200;
  }
}

.data-property__label {
  @apply inline-flex items-center gap-1.5 text-gray-600;
}

.data-property__value {
  @apply font-medium flex items-center gap-1.5 text-base-content;

  &.is-truncated {
    @apply overflow-hidden;

    span {
      @apply inline-block truncate;
    }
  }
}

.data-property__skeleton {
  @apply h-5 w-auto;
}
</style>
